import { isResidentialBien, isLandBien, bienCategoryLabel } from "../Services/bienEditModel";
import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import { useFavorite } from "../context/EngagementContext";
import React, { memo, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  LuArrowRight,
  LuLoaderCircle,
  LuBath,
  LuBedDouble,
  LuCalendarDays,
  LuCar,
  LuHeart,
  LuImage,
  LuMapPin,
  LuMaximize2,
  LuPencil,
  LuTag,
  LuTrash2,
} from "react-icons/lu";

import { photoUrl } from "../Variables.jsx";

const FALLBACK_IMAGE =
  "/image-placeholder.svg";

function ListBiens({
  listing,
  onDelete,
  onEdit,
  isHome = false,
}) {
  const navigate = useNavigate();
  const isLand = isLandBien(listing?.category ?? listing?.categorie ?? listing?.typeBien);
  const { t, i18n } = useTranslation();

  const { isFavorite, toggleFavorite, favoriteLoading } = useFavorite("bien", listing);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const listingId =
    listing?.id ??
    listing?.ID ??
    listing?.bienID ??
    listing?.BienID;

  /*
   * Images
   */
  const images = useMemo(() => {
    if (!listing?.urlImages) {
      return [];
    }

    if (Array.isArray(listing.urlImages)) {
      return listing.urlImages
        .map((image) => String(image).trim())
        .filter(Boolean);
    }

    return String(listing.urlImages)
      .split(",")
      .map((image) => image.trim())
      .filter(Boolean);
  }, [listing?.urlImages]);

  const imageUrl = useMemo(() => {
    if (imageError || images.length === 0) {
      return FALLBACK_IMAGE;
    }

    const firstImage = images[0];

    if (
      firstImage.startsWith("http://") ||
      firstImage.startsWith("https://") ||
      firstImage.startsWith("data:")
    ) {
      return firstImage;
    }

    return photoUrl(firstImage, listing?.folder);
  }, [imageError, images, listing?.folder]);

  /*
   * Prix
   */
  const normalPrice = Number(listing?.prix) || 0;
  const reducedPrice = Number(listing?.prixReduit) || 0;

  const hasOffer =
    Boolean(listing?.offre) &&
    normalPrice > 0 &&
    reducedPrice > 0 &&
    reducedPrice < normalPrice;

  const displayedPrice = hasOffer
    ? reducedPrice
    : normalPrice;

  const discountPercentage =
    hasOffer && normalPrice > 0
      ? Math.round(
          ((normalPrice - reducedPrice) / normalPrice) *
            100
        )
      : 0;

  const formatPrice = (price) =>
    new Intl.NumberFormat(uiI18n.language).format(
      Number(price) || 0
    );

  /*
   * Type de l’annonce
   */
  const listingType = String(
    listing?.type || ""
  ).toLowerCase();

  const isRent = [
    "rent",
    "location",
    "louer",
  ].includes(listingType);

  const isSale = [
    "sale",
    "vente",
    "vendre",
  ].includes(listingType);

  /*
   * Catégorie
   * Masque undefined, null et les valeurs vides.
   */
  const rawCategory =
    listing?.categorie ??
    listing?.category ??
    listing?.typeBien ??
    listing?.typeProperty ??
    "";

  const category = useMemo(() => {
    const value = String(rawCategory || "").trim();

    if (
      !value ||
      value.toLowerCase() === "undefined" ||
      value.toLowerCase() === "null"
    ) {
      return "";
    }

    return value;
  }, [rawCategory]);

  /*
   * Localisation
   */
  const location = [
    t(`delegations.${listing?.ville}.${listing?.delegation}`, listing?.delegation || ""),
    t(`cities.${listing?.ville}`, listing?.ville || ""),
  ]
    .filter((value) => {
      if (!value) {
        return false;
      }

      const normalizedValue = String(value)
        .trim()
        .toLowerCase();

      return (
        normalizedValue !== "undefined" &&
        normalizedValue !== "null"
      );
    })
    .join(", ");

  /*
   * Surface
   */
  const surface =
    listing?.surface ??
    listing?.superficie ??
    listing?.area ??
    null;

  /*
   * Date de publication
   * Ajoute ici le nom utilisé par ton API si nécessaire.
   */
  const publicationDateValue =
    listing?.dateCreation ??
    listing?.createdAt ??
    listing?.creationDate ??
    listing?.datePublication ??
    listing?.createdDate ??
    listing?.dateAjout ??
    listing?.date ??
    null;

  const publicationDate = useMemo(() => {
    if (!publicationDateValue) {
      return null;
    }

    const parsedDate = new Date(publicationDateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }, [publicationDateValue]);

  /*
   * Libellé relatif :
   * "il y a 15 minutes"
   * "il y a 3 heures"
   * "il y a 2 jours"
   *
   * Après 30 jours, affiche la date courte.
   */
  const publicationLabel = useMemo(() => {
    if (!publicationDate) {
      return "";
    }

    const languageCode =
      i18n.resolvedLanguage?.split("-")[0] ||
      i18n.language?.split("-")[0] ||
      "fr";

    const locale =
      languageCode === "ar"
        ? "ar-TN"
        : languageCode === "en"
          ? "en-GB"
          : "fr-FR";

    const now = Date.now();
    const publicationTime = publicationDate.getTime();
    const difference = Math.max(
      0,
      now - publicationTime
    );

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    const relativeFormatter =
      new Intl.RelativeTimeFormat(locale, {
        numeric: "always",
      });

    let label;

    if (difference < hour) {
      const minutes = Math.max(
        1,
        Math.floor(difference / minute)
      );

      label = relativeFormatter.format(
        -minutes,
        "minute"
      );
    } else if (difference < day) {
      const hours = Math.max(
        1,
        Math.floor(difference / hour)
      );

      label = relativeFormatter.format(
        -hours,
        "hour"
      );
    } else if (difference < 30 * day) {
      const days = Math.max(
        1,
        Math.floor(difference / day)
      );

      label = relativeFormatter.format(-days, "day");
    } else {
      label = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(publicationDate);
    }

    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [publicationDate, i18n.language, i18n.resolvedLanguage]);

  const isNewListing = useMemo(() => {
    if (!publicationDate) {
      return false;
    }

    const difference =
      Date.now() - publicationDate.getTime();

    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    return difference >= 0 && difference <= sevenDays;
  }, [publicationDate]);

  /*
   * Navigation et actions
   */
  const openListing = () => {
    if (!listingId) {
      return;
    }

    navigate(`/listing/${listingId}`);
  };

  const handleCardKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      openListing();
    }
  };

  const handleFavoriteClick = (event) => {
    event.stopPropagation();

    toggleFavorite();
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    onEdit?.(listing);
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    onDelete?.(listing);
  };

  return (
    <li className="h-full list-none">
      <article
        role="link"
        tabIndex={0}
        onClick={openListing}
        onKeyDown={handleCardKeyDown}
        aria-label={`${t(
          isLand ? "listing.viewLand" : "listing.viewDetails",
          "Voir l’annonce"
        )} ${listing?.libelle || ""}`}
        className="lina-property-card
          group
          flex
          h-full
          cursor-pointer
          flex-col
          overflow-hidden
          rounded-2xl
          border
          border-slate-200
          bg-white
          shadow-sm
          transition-all
          duration-300
          hover:-translate-y-1
          hover:border-green-200
          hover:shadow-lg
          focus:outline-none
          focus:ring-4
          focus:ring-green-500/15
        "
      >
        {/* Image */}
        <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-slate-100">
          {!imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-slate-200" />
          )}

          <img
            src={imageUrl}
            alt={
              listing?.libelle ||
              uiI18n.t("ui.lina_immo_property_listing")
            }
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(true);
            }}
            className={`
              h-full
              w-full
              object-cover
              transition-all
              duration-500
              group-hover:scale-105
              ${
                imageLoaded
                  ? "opacity-100"
                  : "opacity-0"
              }
            `}
          />

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-slate-950/5" />

          {/* Badges du haut */}
          <div className="absolute start-3 top-3 flex max-w-[72%] flex-wrap gap-1.5">
            <span
              className={`
                inline-flex
                items-center
                gap-1
                rounded-full
                px-2.5
                py-1
                text-[10px]
                font-black
                uppercase
                tracking-wide
                text-white
                shadow-sm
                ${
                  isRent
                    ? "bg-blue-600"
                    : "bg-green-600"
                }
              `}
            >
              <LuTag size={11} />

              {isRent
                ? t("listing.rent", "Location")
                : isSale
                  ? t("listing.sale", "Vente")
                  : t(
                      "listing.available",
                      "Disponible"
                    )}
            </span>

            {category && (
              <span className="rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold text-slate-700 shadow-sm">
                {bienCategoryLabel(t, category)}
              </span>
            )}

            {isNewListing && (
              <span className="rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-black uppercase text-slate-950 shadow-sm">
                {t("listing.new", "Nouveau")}
              </span>
            )}

            {hasOffer && (
              <span className="rounded-full bg-red-500 px-2.5 py-1 text-[10px] font-black text-white shadow-sm">
                -{discountPercentage} %
              </span>
            )}
          </div>

          {/* Favori */}
        <button
  type="button"
  disabled={favoriteLoading}
  onClick={handleFavoriteClick}
  aria-pressed={isFavorite}
  aria-label={
    isFavorite
      ? uiI18n.t("remove_from_favorites")
      : uiI18n.t("add_to_favorites")
  }
  title={
    isFavorite
      ? uiI18n.t("remove_from_favorites")
      : uiI18n.t("add_to_favorites")
  }
  className="
    group
    inline-flex
    h-10
    items-center
    justify-center
    gap-2
    rounded-xl
    border
    border-slate-200
    bg-white
    px-4
    text-sm
    font-semibold
    text-slate-700
    shadow-sm
    transition-all
    duration-200

    hover:border-green-200
    hover:bg-green-50
    hover:text-green-700
    hover:shadow

    focus:outline-none
    focus:ring-2
    focus:ring-green-500/20

    disabled:cursor-not-allowed
    disabled:opacity-60
    disabled:hover:border-slate-200
    disabled:hover:bg-white
    disabled:hover:text-slate-700
    disabled:hover:shadow-sm
  "
>
  {favoriteLoading ? (
    <>
      <LuLoaderCircle
        size={18}
        strokeWidth={2}
        className="animate-spin"
      />

      <span>{uiI18n.t("saving")}</span>
    </>
  ) : (
    <>
      <LuHeart
        size={18}
        strokeWidth={2}
        fill={isFavorite ? "currentColor" : "none"}
        className={`
          transition-transform
          duration-200
          group-hover:scale-110
          ${
            isFavorite
              ? "text-red-500 group-hover:text-green-700"
              : ""
          }
        `}
      />

      <span>
        {isFavorite
          ? uiI18n.t("in_my_favorites")
          : uiI18n.t("add_to_favorites")}
      </span>
    </>
  )}
</button>
          {/* Date en bas à gauche */}
          {publicationLabel && (
            <span
              className="
                absolute
                bottom-3
                start-3
                inline-flex
                items-center
                gap-1.5
                rounded-full
                bg-slate-950/75
                px-2.5
                py-1
                text-[11px]
                font-semibold
                text-white
                shadow-sm
                backdrop-blur
              "
            >
              <LuCalendarDays size={13} />

              {publicationLabel}
            </span>
          )}

          {/* Nombre de photos en bas à droite */}
          {images.length > 0 && (
            <span
              className="
                absolute
                bottom-3
                end-3
                inline-flex
                items-center
                gap-1
                rounded-full
                bg-slate-950/75
                px-2.5
                py-1
                text-[11px]
                font-bold
                text-white
                shadow-sm
                backdrop-blur
              "
            >
              <LuImage size={13} />

              {images.length}
            </span>
          )}
        </div>

        {/* Contenu */}
        <div className="flex flex-1 flex-col p-4">
          {/* Prix */}
          {hasOffer && (
            <p className="text-xs font-semibold text-slate-400 line-through">
              {formatPrice(normalPrice)} TND
            </p>
          )}

          {displayedPrice > 0 && <p className="lina-card-price text-xl font-black text-green-600">
            {formatPrice(displayedPrice)}

            <span className="ms-1 text-xs font-bold">
              TND
            </span>

            {isRent && (
              <span className="ms-1 text-xs font-medium text-slate-500">
                / {t("listing.month", "mois")}
              </span>
            )}
          </p>}

          {/* Titre */}
          <h3 className="mt-2 line-clamp-2 text-base font-black leading-6 text-slate-900 transition group-hover:text-green-700">
            {listing?.libelle ||
              t(
                "listing.untitled",
                "Annonce immobilière"
              )}
          </h3>

          {/* Localisation */}
          <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-500">
            <LuMapPin
              size={16}
              className="mt-0.5 shrink-0 text-green-600"
            />

            <span className="lina-card-location">
              {location ||
                t(
                  "listing.locationUnavailable",
                  "Localisation non précisée"
                )}
            </span>
          </p>

          {/* Caractéristiques */}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 pt-3">
            {!isLand && isResidentialBien(listing?.category) && Number(listing?.chambres) > 0 && (
                <PropertyInline
                  icon={LuBedDouble}
                  value={`${listing.chambres} ${t(
                    "listing.bedroomsShort",
                    "ch."
                  )}`}
                />
              )}

            {!isLand && Number(listing?.sallesBains) > 0 && (
                <PropertyInline
                  icon={LuBath}
                  value={`${listing.sallesBains} ${t(
                    "listing.bathroomsShort",
                    "SDB"
                  )}`}
                />
              )}

            {Number(surface) > 0 && (
              <PropertyInline
                icon={LuMaximize2}
                value={`${surface} m²`}
              />
            )}

            {!isLand && Number(listing?.parking) === 1 && (
              <PropertyInline
                icon={LuCar}
                value={t(
                  "listing.parking",
                  "Parking"
                )}
              />
            )}
          </div>

          {/* Actions de gestion */}
          {(onEdit || onDelete) && (
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              {onEdit && (
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 transition hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                >
                  <LuPencil size={15} />

                  {t("edit", "Modifier")}
                </button>
              )}

              {onDelete && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 text-sm font-bold text-red-600 transition hover:bg-red-100"
                >
                  <LuTrash2 size={15} />

                  {t("delete", "Supprimer")}
                </button>
              )}
            </div>
          )}

          {/* Voir l’annonce */}
          {!onEdit && !onDelete && (
            <div className="mt-auto pt-4">
              <span className="inline-flex items-center gap-2 text-sm font-black text-green-600">
                {t(
                  isLand ? "listing.viewLand" : "listing.viewDetails",
                  "Voir l’annonce"
                )}

                <LuArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
                />
              </span>
            </div>
          )}

          {isHome && (
            <span className="sr-only"> {uiI18n.t("ui.listing_displayed_on_the_home_page")} </span>
          )}
        </div>
      </article>
    </li>
  );
}

function PropertyInline({
  icon: Icon,
  value,
}) {
  useUiTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
      <Icon
        size={15}
        className="shrink-0 text-green-600"
      />

      <span className="font-semibold text-slate-600">
        {value}
      </span>
    </span>
  );
}

export default memo(ListBiens);